import axios from "axios";
import { prepareDataExport } from "@/services/dataExport";
import { exportFile } from "@/services/fileExport";
import { useCookies } from "vue3-cookies";
import { store } from "@/store";
import { AuthService } from "@/services/AuthService";
import {
    getBaseUrl,
    getApiRouteV2,
    setAPIBaseUrl,
    setAPICreds,
    setLoggingIn,
    getAPIUrl,
    getAPIUrlV2,
    clearAxiosToken,
    setAxiosToken
} from "@/classes/apiConfig";

// Re-export the api-config helpers for back-compat with any consumer
// still importing them from `@/classes/DataAPI`. New code should import
// directly from `@/classes/apiConfig`.
export {
    setAPIBaseUrl,
    setAPICreds,
    setLoggingIn,
    getAPIUrl,
    getAPIUrlV2,
    clearAxiosToken,
    setAxiosToken
};

var cookies;

let emitter;
let storeRefreshHandlers = [];

function clearStoreRefreshHandlers() {
    if (!emitter) return;
    for (const [event, handler] of storeRefreshHandlers) {
        emitter.off(event, handler);
    }
    storeRefreshHandlers = [];
}

import {
    AIAPI,
    AuditAPI,
    LiveUpdateAPI,
    MeatAPI,
    IngredientAPI,
    IngredientImportAPI,
    IntakeTraceAPI,
    DeliveryAPI,
    DeliveryItemAPI,
    SupplierAPI,
    CustomerAPI,
    HACCPPlanAPI,
    UserAPI,
    TawkAPI,
    MenuItemAPI,
    MenuAPI,
    PackagingAPI,
    FileAPI,
    StorageAPI,
    FoodRecordAPI,
    FoodRecordItemAPI,
    UserClockEventAPI,
    ClientAPI,
    ProductAPI,
    LocationAPI,
    DepartmentAPI,
    TemperatureLocationAPI,
    TemperatureLogAPI,
    SensorLogAPI,
    SensorAPI,
    SensorAlertAPI,
    CheckAPI,
    CheckFormAPI,
    CheckScheduleAPI,
    StockAPI,
    StockOperationAPI,
    WastageAPI,
    RecipeAPI,
    ProductionContextAPI,
    ProductionRunAPI,
    BatchAPI,
    VehicleAPI,
    VanCheckAPI,
    MapsAPI,
    IssueAPI,
    ComplaintAPI,
    ReportAPI,
    TrainingCourseAPI,
    TrainingAssignmentAPI,
    TrainingRecordAPI,
    AnnouncementAPI,
    TaskAPI,
    ChatAPI,
    ShiftAPI,
    RotaEntryAPI,
    DispatchAPI,
    FormLibraryAPI,
    LabelTemplateAPI,
    SearchAPI
} from "./APIs";

export default class DataAPI {
    /** @type {AuthService} Handles all authentication-related operations */
    Auth = null;

    /** @type {AIAPI} Handles interactions with AI-related services. */
    AI = null;

    /** @type {AuditAPI} Manages audit-related data. */
    Audit = null;

    /** @type {LiveUpdateAPI} Manages live updates or real-time updates. */
    LiveUpdate = null;

    /** @type {MeatAPI} Manages meat-related data. */
    Meat = null;

    /** @type {IngredientAPI} Deals with ingredient-related data. */
    Ingredient = null;

    /** @type {IngredientImportAPI} Bulk ingredient/stock spreadsheet import. */
    IngredientImport = null;

    /** @type {IntakeTraceAPI} Cross-intake lookup for trace/mass-balance entry. */
    IntakeTrace = null;

    /** @type {DeliveryAPI} Handles delivery-related operations. */
    Delivery = null;

    /** @type {DeliveryItemAPI} Manages individual delivery items. */
    DeliveryItem = null;

    /** @type {SupplierAPI} Manages suppliers. */
    Supplier = null;

    /** @type {CustomerAPI} Manages customer-related operations. */
    Customer = null;

    /** @type {UserAPI} Handles user-related operations. */
    User = null;

    /** @type {TawkAPI} Handles Tawk.to secure-mode auth. */
    Tawk = null;

    /** @type {MenuItemAPI} Manages menu items related to food offerings. */
    MenuItem = null;

    /** @type {MenuAPI} Manages menus (typed, categorised; see allergens-menus spec). */
    Menu = null;

    /** @type {PackagingAPI} Manages packaging data or operations. */
    Packaging = null;

    /** @type {FileAPI} Handles file operations, such as uploading or retrieving files. */
    File = null;

    /** @type {StorageAPI} Manages Google Cloud Storage files operations. */
    Storage = null;

    /** @type {FoodRecordAPI} Handles food temperature records. */
    FoodRecord = null;

    /** @type {FoodRecordItemAPI} Manages individual food record items. */
    FoodRecordItem = null;

    /** @type {UserClockEventAPI} Handles clock-in and clock-out events for users. */
    UserClockEvent = null;

    /** @type {ClientAPI} Manages client-related operations. */
    Client = null;

    /** @type {HACCPPlanAPI} Manages HACCP plans, important for food safety. */
    HACCPPlan = null;

    /** @type {ProductAPI} Handles product-related operations. */
    Product = null;

    /** @type {LocationAPI} Manages customer locations. */
    Location = null;

    /** @type {DepartmentAPI} Handles customer departments. */
    Department = null;

    /** @type {TemperatureLocationAPI} Manages locations where temperature control is performed. */
    TemperatureLocation = null;

    /** @type {TemperatureLogAPI} Handles logs of temperature data from temperature locations. */
    TemperatureLog = null;

    /** @type {SensorLogAPI} Manages logs from sensors. */
    SensorLog = null;
    Sensor = null;
    SensorAlert = null;

    /** @type {CheckAPI} Handles operational checks. */
    Check = null;

    /** @type {CheckFormAPI} Manages check forms. */
    CheckForm = null;

    /** @type {CheckScheduleAPI} Manages schedules for checks. */
    CheckSchedule = null;

    /** @type {StockAPI} Handles stock-related operations. */
    Stock = null;

    /** @type {StockOperationAPI} Manages specific operations related to stock. */
    StockOperation = null;

    /** @type {WastageAPI} Manages wastage tracking operations. */
    Wastage = null;

    /** @type {RecipeAPI} Handles recipe-related data. */
    Recipe = null;

    /** @type {ProductionContextAPI} Server-assembled view for the production form (recipes + stocks + intakes + suppliers). */
    ProductionContext = null;

    /** @type {ProductionRunAPI} Atomic multi-recipe production commit. */
    ProductionRun = null;
    Batch = null;

    /** @type {VehicleAPI} Manages vehicle-related operations. */
    Vehicle = null;

    /** @type {VanCheckAPI} A specialized API for checking vans. */
    VanCheck = null;

    /** @type {MapsAPI} Handles map-related operations. */
    Maps = null;

    /** @type {IssueAPI} Manages issue-related operations. */
    Issue = null;

    /** @type {ComplaintAPI} Manages complaint-related operations. */
    Complaint = null;

    /** @type {ReportAPI} Handles report-related operations like counts. */
    Report = null;

    /** @type {TrainingCourseAPI} Manages training courses. */
    TrainingCourse = null;

    /** @type {TrainingAssignmentAPI} Manages training assignments to users. */
    TrainingAssignment = null;

    /** @type {TrainingRecordAPI} Manages administrator-recorded training status events. */
    TrainingRecord = null;

    /** @type {AnnouncementAPI} Manages team announcements and communications. */
    Announcement = null;
    Task = null;

    /** @type {ChatAPI} Tenant team conversations. */
    Chat = null;

    /** @type {ShiftAPI} Manages work shift templates. */
    Shift = null;

    /** @type {RotaEntryAPI} Manages rota/schedule entries for employees. */
    RotaEntry = null;

    /** @type {DispatchAPI} Manages product dispatch operations. */
    Dispatch = null;

    /** @type {FormLibraryAPI} Manages the shared form library. */
    FormLibrary = null;

    /** @type {LabelTemplateAPI} Manages HTML/CSS label templates rendered via Gotenberg. */
    LabelTemplate = null;

    /** @type {SearchAPI} Searches compact tenant business records. */
    Search = null;

    /**
     * Initializes all API instances.
     * @param {Object} emitterInstance - The event emitter instance.
     */
    constructor(emitterInstance) {
        emitter = emitterInstance;
        this.Auth = new AuthService(emitter);

        // APIs init
        this.AI = new AIAPI(emitter, this.Auth);
        this.Audit = new AuditAPI(emitter);
        this.LiveUpdate = new LiveUpdateAPI(emitter, this.Auth);
        this.Meat = new MeatAPI(emitter);
        this.Ingredient = new IngredientAPI(emitter);
        this.IngredientImport = new IngredientImportAPI(emitter);
        this.IntakeTrace = new IntakeTraceAPI(emitter);
        this.Delivery = new DeliveryAPI(emitter);
        this.DeliveryItem = new DeliveryItemAPI(emitter);
        this.Supplier = new SupplierAPI(emitter);
        this.Customer = new CustomerAPI(emitter);
        this.User = new UserAPI(emitter);
        this.Tawk = new TawkAPI(emitter);
        this.MenuItem = new MenuItemAPI(emitter);
        this.Menu = new MenuAPI(emitter);
        this.Packaging = new PackagingAPI(emitter);
        this.File = new FileAPI(emitter);
        this.Storage = new StorageAPI(emitter);
        this.FoodRecord = new FoodRecordAPI(emitter);
        this.FoodRecordItem = new FoodRecordItemAPI(emitter);
        this.UserClockEvent = new UserClockEventAPI(emitter);
        this.Client = new ClientAPI(emitter);
        this.HACCPPlan = new HACCPPlanAPI(emitter);
        this.Product = new ProductAPI(emitter);
        this.Location = new LocationAPI(emitter);
        this.Department = new DepartmentAPI(emitter);
        this.TemperatureLocation = new TemperatureLocationAPI(emitter);
        this.TemperatureLog = new TemperatureLogAPI(emitter);
        this.SensorLog = new SensorLogAPI(emitter);
        this.Sensor = new SensorAPI(emitter);
        this.SensorAlert = new SensorAlertAPI(emitter);
        this.Check = new CheckAPI(emitter, this);
        this.CheckForm = new CheckFormAPI(emitter);
        this.CheckSchedule = new CheckScheduleAPI(emitter);
        this.Stock = new StockAPI(emitter);
        this.StockOperation = new StockOperationAPI(emitter);
        this.Wastage = new WastageAPI(emitter);
        this.Recipe = new RecipeAPI(emitter);
        this.ProductionContext = new ProductionContextAPI(emitter);
        this.ProductionRun = new ProductionRunAPI(emitter);
        this.Batch = new BatchAPI(emitter);
        this.Vehicle = new VehicleAPI(emitter);
        this.VanCheck = new VanCheckAPI(emitter);
        this.Maps = new MapsAPI(emitter);
        this.Issue = new IssueAPI(emitter);
        this.Complaint = new ComplaintAPI(emitter);
        this.Report = new ReportAPI(emitter);
        this.TrainingCourse = new TrainingCourseAPI(emitter);
        this.TrainingAssignment = new TrainingAssignmentAPI(emitter);
        this.TrainingRecord = new TrainingRecordAPI(emitter);
        this.Announcement = new AnnouncementAPI(emitter);
        this.Task = new TaskAPI(emitter);
        this.Chat = new ChatAPI(emitter);
        this.Shift = new ShiftAPI(emitter);
        this.RotaEntry = new RotaEntryAPI(emitter);
        this.Dispatch = new DispatchAPI(emitter);
        this.FormLibrary = new FormLibraryAPI(emitter);
        this.LabelTemplate = new LabelTemplateAPI(emitter);
        this.Search = new SearchAPI(emitter);

        // Set up auth service dependencies
        this.Auth.User = this.User;
        this.Auth.Location = this.Location;
        this.Auth.Department = this.Department;

        cookies = useCookies().cookies;

        // Store refresh listeners — keeps API classes decoupled from Vuex
        const storeRefreshMap = [
            ["user-add", "refreshUserList"],
            ["user-update", "refreshUserList"],
            ["user-rate-update", "refreshUserList"],
            ["user-delete", "refreshUserList"],
            ["user-archive", "refreshUserList"],
            ["user-deactivate", "refreshUserList"],
            ["check-form-create", "refreshCheckFormList"],
            ["check-form-archive", "refreshCheckFormList"],
            ["check-form-unarchive", "refreshCheckFormList"],
            ["check-form-mark-archived", "refreshCheckFormList"],
            ["temperature-location-add", "refreshTemperatureLocationList"],
            ["temperature-location-update", "refreshTemperatureLocationList"],
            ["temperature-location-delete", "refreshTemperatureLocationList"],
            ["temperature-location-mark-archived", "refreshTemperatureLocationList"]
        ];

        clearStoreRefreshHandlers();
        for (const [event, action] of storeRefreshMap) {
            const handler = async () => {
                // mitt does not await listener promises. A failed background
                // compatibility read must not reject the acknowledged write.
                try {
                    await store.dispatch(action);
                } catch {
                    /* Canonical queries retain their own load errors. */
                }
            };
            emitter.on(event, handler);
            storeRefreshHandlers.push([event, handler]);
        }
    }

    // Delegate auth methods to AuthService
    async checkAuthWithoutLogout() {
        return this.Auth.checkAuthWithoutLogout();
    }

    async isTokenValid(token) {
        return this.Auth.isTokenValid(token);
    }

    async login(user, pass, route) {
        if (await this.checkAuthWithoutLogout()) {
            emitter.emit("login", { route, loginMethod: "existing" });
            Promise.resolve(this.User.SetActiveStatus(true)).catch(() => {});
            return;
        }

        const response = await this.Auth.login(user, pass);
        const userID = response.id;
        const token = response.access_token || response.token;

        await this.Auth.setTokens(token, response.refresh_token);
        await this.Auth.setUserData(userID, token);

        if (await this.checkAuthWithoutLogout()) {
            emitter.emit("login", {
                route,
                loginMethod: "password",
                refreshToken: response.refresh_token,
                userID: String(userID)
            });
            Promise.resolve(this.User.SetActiveStatus(true)).catch(() => {});
            return;
        }
    }

    async quickLogin(userID, pin, route) {
        if (await this.checkAuthWithoutLogout()) {
            return;
        }

        const response = await this.Auth.quickLogin(userID, pin);
        const token = response.access_token || response.token;

        await this.Auth.setTokens(token, response.refresh_token);
        await this.Auth.setUserData(userID, token);

        if (await this.checkAuthWithoutLogout()) {
            emitter.emit("login", {
                route,
                loginMethod: "quick-login",
                refreshToken: response.refresh_token,
                userID: String(userID)
            });
            Promise.resolve(this.User.SetActiveStatus(true)).catch(() => {});
            return;
        }
    }

    async logoutUser() {
        this.AI.Close?.();
        this.LiveUpdate.Close?.();
        await this.Auth.logout();
    }

    async clearAuth() {
        this.AI.Close?.();
        this.LiveUpdate.Close?.();
        await this.Auth.clearAuth();
    }

    requestPasswordReset(emailAddress) {
        return this.Auth.requestPasswordReset(emailAddress);
    }

    resetPassword(passwordResetData, authToken) {
        return this.Auth.resetPassword(passwordResetData, authToken);
    }

    changePassword(data) {
        return this.Auth.changePassword(data);
    }

    // DATA QUERIES

    sendMail(data) {
        return new Promise((resolve, reject) => {
            axios
                .post(`${getBaseUrl()}/email`, data)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    getSupportPin() {
        return new Promise((resolve, reject) => {
            axios
                .get(`${getBaseUrl()}/supportpin`)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    prepareDataExport(options = {}) {
        return prepareDataExport({
            ...options,
            baseUrl: `${getBaseUrl()}/${getApiRouteV2()}`,
            tenant: String(axios.defaults.headers.common["Customer-ID"] || ""),
            actor: this.Auth.getUserIDFromToken(store.state.token) || "unknown"
        });
    }

    exportAllData() {
        return new Promise((resolve, reject) => {
            axios
                .get(`${getBaseUrl()}/${getApiRouteV2()}/export`, {
                    responseType: "blob",
                    timeout: 0 // full-tenant ZIP export can take minutes — no global timeout
                })
                .then(async (response) => {
                    // Extract filename from Content-Disposition header if available
                    const contentDisposition = response.headers["content-disposition"];
                    let filename = "data-export.zip";

                    if (contentDisposition) {
                        const filenameMatch = contentDisposition.match(
                            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
                        );
                        if (filenameMatch && filenameMatch[1]) {
                            filename = filenameMatch[1].replace(/['"]/g, "");
                        }
                    }

                    if (
                        !String(response.headers["content-type"] || "").startsWith(
                            "application/zip"
                        )
                    )
                        throw new Error("The server did not return a complete ZIP archive.");
                    const result = await exportFile(
                        new Blob([response.data], { type: "application/zip" }),
                        filename
                    );
                    resolve({ ...result, success: result.mode !== "cancelled", filename });
                })
                .catch(async (err) => {
                    if (
                        err.response?.data instanceof Blob &&
                        err.response.data.size < 1024 * 1024
                    ) {
                        try {
                            err.response.data = JSON.parse(await err.response.data.text());
                        } catch {
                            /* Keep the original HTTP failure. */
                        }
                    }
                    reject(err);
                });
        });
    }
}
